/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import {
	CosmosEnumIconPosition,
	CosmosEnumUserProfileVariant,
	CosmosUserProfileFragment,
} from "@cosmos/hygraph-schema";

import {
	CosmosAsset,
	CosmosAvatar,
	CosmosButton,
	CosmosCard,
	CosmosTextVariantEnum,
	LogoTitleDescButtonStack,
} from "@components/elements";
import { css, cva, cx } from "@panda/css";

const styles = css({
	padding: "eds.dimension.32",
	"&& .cosmos-card__content": {
		paddingBlockStart: "0",
		"& .logo img": {
			maxHeight: "eds.dimension.18",
		},
		"& .user-profile-element-avatar span": {
			width: "eds.dimension.76 !important",
			height: "eds.dimension.76 !important",
		},
		"& svg": {
			maxWidth: "eds.dimension.20",
			maxHeight: "eds.dimension.20",
		},
		"& .user-profile-element-text > h3": {
			marginBlock: "0",
			maxWidth: "100%",
		},
	},
});

const ContentStyles = cva({
	base: {
		alignItems: "start",
		display: "flex",
		justifyContent: "space-between",
		flexGrow: "1",
		minW: "0",
		height: "100%",
		maxW: "100%",
		gap: "eds.dimension.20",
		sm: {
			gap: "eds.dimension.24",
		},
	},
	variants: {
		variant: {
			avatar: {
				flexDirection: "column",
				sm: {
					flexDirection: "row",
					flexWrap: "wrap",
					"& .logo-title-desc-button-stack": {
						flexGrow: "1",
						flexBasis: "min-content",
					},
					"& .user-profile-element-avatar": {
						alignSelf: "center",
					},
				},
			},
			vertical: {
				flexDirection: "column",
			},
			horizontal: {
				flexDirection: "column",
				sm: {
					flexDirection: "row",
					flexWrap: "wrap",
					"& .logo-title-desc-button-stack": {
						flexGrow: "1",
						flexBasis: "min-content",
					},
				},
			},
		},
	},
});

const AssetStyles = css({
	borderRadius: "eds.dimension.16",
	maxW: "min(350px, 100%)",
	maxH: "350px",
	width: "auto",
	height: "auto",
});

export function UserProfile({
	avatar,
	logoImage,
	titleText,
	desc,
	variant,
	button,
}: CosmosUserProfileFragment): React.JSX.Element {
	if (!variant) variant = CosmosEnumUserProfileVariant.Avatar;
	const altText = avatar?.label || titleText || avatar?.imageAsset?.altText || "";
	if (altText && avatar && !avatar?.label) avatar.label = altText;

	return (
		<CosmosCard className={cx("user-profile-element", styles)} variant="outlinedThin" direction="row" borderRadius={24}>
			<CosmosCard.Content className={cx("user-profile-element-content", ContentStyles({ variant }))}>
				{variant === CosmosEnumUserProfileVariant.Avatar && (
					<CosmosAvatar {...avatar} className="user-profile-element-avatar" size="2xl" hideLabel={true} />
				)}
				{variant !== CosmosEnumUserProfileVariant.Avatar && (
					<CosmosAsset
						asset={{
							...avatar?.imageAsset,
							altText,
							url: avatar?.imageSrc || avatar?.imageAsset?.url || "",
							optUrl: avatar?.imageSrc || avatar?.imageAsset?.optUrlMd || avatar?.imageAsset?.optUrl || "",
							id: avatar?.imageAsset?.id || "",
						}}
						attributionClassName={AssetStyles}
						className={cx("user-profile-element-image", AssetStyles)}
					/>
				)}
				<LogoTitleDescButtonStack
					animate={false}
					stack={{ gap: "eds.dimension.8", maxWidth: "100%" }}
					image={logoImage}
					titleDescStack={{
						stack: { gap: "eds.dimension.8", align: "start" },
						title: { text: titleText, variant: CosmosTextVariantEnum.headingMD, as: "h3" },
						description: {
							html: desc,
							variant: CosmosTextVariantEnum.paragraphSM,
							as: "div",
							color: "eds.text.secondary",
						},
						className: "user-profile-element-text",
					}}
				/>
				<CosmosButton
					{...button}
					iconPosition={button?.iconPosition || CosmosEnumIconPosition.End}
					className="user-profile-element-button"
				/>
			</CosmosCard.Content>
		</CosmosCard>
	);
}
